import { useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { StatusCodes } from '@constants/enum/common';
import {
  activatePipeline as activatePipelineAction,
  createPipeline as createPipelineAction,
  deletePipeline as deletePipelineAction,
  deployPipeline as deployPipelineAction,
  fetchPipeline as fetchPipelineAction,
  FetchPipelineProps,
  getRuntimeIssueDetection as getRuntimeIssueDetectionAction,
  resetMessage as resetMessageAction,
  setActiveTabPipelineDetailsPage as setActiveTabPipelineDetailsPageAction,
  setPipelineEditedStateTo as setPipelineEditedStateToAction,
  setPipelineName as setPipelineNameAction,
  setPipelineQueryYaml as setPipelineQueryYamlAction,
  setPipelineYaml as setPipelineYamlAction,
  startPollingPipelineStatus as startPollingPipelineStatusAction,
  stopPollingPipelineStatus as stopPollingPipelineStatusAction,
  undeployPipeline as undeployPipelineAction,
  updatePipeline as updatePipelineAction,
  updatePipelineYaml as updatePipelineYamlAction,
  validatePipelineYaml as validatePipelineYamlAction,
} from '@redux/actions/pipelineActions';
import {
  activatePipelineActionStatusSelector,
  newPipelineNameSelector,
  pipelineActionStatusSelector,
  pipelineCreateUpdatePipelineStatusSelector,
  pipelineErrorsSelector,
  pipelineLogsUnhealthySelector,
  pipelineMessageSelector,
  pipelineSelector,
  pipelineStatusSelector,
  renamePipelineNameStatusSelector,
} from '@redux/selectors/pipelineSelectors';
import { IMessage, IPipeline } from '@redux/types/types';
import { useStateCleanup } from './useStateCleanup';

const usePipelineActions = () => {
  const dispatch = useDispatch();
  const pipeline: IPipeline = useSelector(pipelineSelector);
  const renamePipelineNameStatus: StatusCodes = useSelector(renamePipelineNameStatusSelector);
  const status: StatusCodes = useSelector(pipelineStatusSelector);
  const actionStatus: StatusCodes = useSelector(pipelineActionStatusSelector);
  const activatePipelineActionStatus: StatusCodes = useSelector(
    activatePipelineActionStatusSelector,
  );
  const message: IMessage = useSelector(pipelineMessageSelector);
  const pipelineLogsUnhealthy: Record<string, boolean> = useSelector(pipelineLogsUnhealthySelector);
  const pipelineCreateUpdatePipelineStatus: StatusCodes = useSelector(
    pipelineCreateUpdatePipelineStatusSelector,
  );
  const newPipelineName: string = useSelector(newPipelineNameSelector);
  const pipelineErrors = useSelector(pipelineErrorsSelector);

  const { reset } = useStateCleanup();

  const getPipelineStudioPageRoute = (pipelineName: string) => {
    return `/pipelines/${pipelineName}/builder`;
  };

  const getPipelineDetailsPageRoute = (pipelineName: string) => {
    return `/pipelines/${pipelineName}`;
  };

  const createPipeline = async (payload: { code?: string; pipelineName: string }) => {
    await dispatch(createPipelineAction(payload));
  };

  const deployPipeline = async (pipelineName: string) => {
    await dispatch(deployPipelineAction(pipelineName));
  };

  const undeployPipeline = async (pipelineName: string) => {
    await dispatch(undeployPipelineAction(pipelineName));
  };

  const activatePipeline = async (pipelineName: string) => {
    await dispatch(activatePipelineAction({ pipelineName }));
  };

  const deletePipeline = async (pipelineName: string) => {
    await dispatch(deletePipelineAction(pipelineName));
  };

  const updatePipeline = async (payload: { pipelineName: string; newPipelineName: string }) => {
    await dispatch(updatePipelineAction(payload));
  };

  const setActiveTabPipelineDetailsPage = (tabKey: string) => {
    dispatch(setActiveTabPipelineDetailsPageAction(tabKey));
  };

  const resetMessage = () => {
    dispatch(resetMessageAction);
  };

  const fetchPipeline = useCallback(
    (args: FetchPipelineProps) => {
      dispatch(fetchPipelineAction(args));
    },
    [dispatch],
  );

  const startPollingPipelineStatus = (pipelineName: string, isExternal?: boolean) => {
    dispatch(startPollingPipelineStatusAction({ pipelineName, isExternal }));
  };

  const stopPollingPipelineStatus = () => {
    dispatch(stopPollingPipelineStatusAction());
  };

  const setPipelineName = (pipelineName: string) => {
    dispatch(setPipelineNameAction(pipelineName));
  };

  const getRuntimeIssueDetection = (pipelineName: string) => {
    dispatch(getRuntimeIssueDetectionAction(pipelineName));
  };

  const validatePipelineYaml = (payload: {
    yamlCode?: string;
    indexingYaml?: string;
    queryYaml?: string | null;
  }) => {
    dispatch(
      validatePipelineYamlAction({
        config: payload.yamlCode,
        indexing_yaml: payload.indexingYaml,
        query_yaml: payload.queryYaml,
      }),
    );
  };

  const setPipelineYaml = (yaml: { queryYaml: string }) => {
    dispatch(setPipelineYamlAction(yaml));
  };

  const setPipelineEditedStateTo = (edited: boolean) => {
    dispatch(setPipelineEditedStateToAction(edited));
  };

  // API update
  const updatePipelineYaml = async (
    payload: { code: string; name: string } | { query_yaml: string; name: string },
  ) => {
    await dispatch(updatePipelineYamlAction(payload));
  };

  const setQueryPipelineYaml = (yaml: string) => {
    dispatch(setPipelineQueryYamlAction(yaml));
  };

  // Rest handlers

  const resetPipelineDetailsState = () => {
    reset([
      'pipelineStore.pipeline',
      'pipelineStore.pipelineStatistics',
      'pipelineStore.pipelineStatisticsAnswers',
      'pipelineStore.pipelineLatestQueries',
      'pipelineStore.pipelineLogs',
      'pipelineStore.pipelineLogsStatus',
      'pipelineStore.activeTabPipelineDetailsPage',
      'pipelineStore.pipelineErrors',
      'pipelineStore.status',
      'pipelineStore.actionStatus',
      'pipelineStore.createUpdatePipelineStatus',
      'pipelineStore.loadingLatestQueriesStatus',
    ]);
  };

  const resetPipeline = () => {
    reset(['pipelineStore.pipeline', 'pipelineStore.createUpdatePipelineStatus']);
  };

  const resetPipelineErrors = useCallback(() => {
    reset(['pipelineStore.pipelineErrors']);
  }, [reset]);

  const resetRenamePipelineNameStatus = () => {
    reset(['pipelineStore.renamePipelineNameStatus', 'pipelineStore.message']);
  };

  const resetNewPipelineName = () => {
    reset(['pipelineStore.newPipelineName']);
  };

  return {
    pipeline,
    status,
    actionStatus,
    activatePipelineActionStatus,
    message,
    pipelineLogsUnhealthy,
    renamePipelineNameStatus,
    pipelineCreateUpdatePipelineStatus,
    newPipelineName,
    pipelineErrors,
    createPipeline,
    deployPipeline,
    undeployPipeline,
    activatePipeline,
    deletePipeline,
    updatePipeline,
    setActiveTabPipelineDetailsPage,
    resetRenamePipelineNameStatus,
    resetMessage,
    resetNewPipelineName,
    resetPipelineErrors,
    resetPipeline,
    fetchPipeline,
    startPollingPipelineStatus,
    stopPollingPipelineStatus,
    setPipelineName,
    getRuntimeIssueDetection,
    validatePipelineYaml,
    setPipelineYaml,
    setPipelineEditedStateTo,
    updatePipelineYaml,
    setQueryPipelineYaml,
    getPipelineStudioPageRoute,
    resetPipelineDetailsState,
    getPipelineDetailsPageRoute,
  };
};

export default usePipelineActions;
